import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { useState } from "react";
import { Loader2, CheckCircle2, Building2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

const PARTNER_TYPES = [
  { id: "banco_talentos", label: "Acesso ao banco de talentos" },
  { id: "vagas", label: "Publicar vagas" },
  { id: "doacao_financeira", label: "Doação financeira" },
  { id: "equipamentos", label: "Doação de equipamentos" },
  { id: "mentoria", label: "Mentoria" },
  { id: "patrocinio", label: "Patrocínio de turmas" },
];

export default function EmpresaSolicitarParceria() {
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    companyName: "",
    industry: "",
    contactName: "",
    contactEmail: "",
    contactPhone: "",
    website: "",
    city: "",
    state: "",
    message: "",
  });
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [emailError, setEmailError] = useState("");

  const toggleType = (id: string) => {
    setSelectedTypes((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]
    );
  };

  const handleChange = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const canSubmit =
    form.companyName.trim() &&
    form.contactName.trim() &&
    form.contactEmail.trim() &&
    selectedTypes.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setEmailError("");
    setIsLoading(true);
    try {
      // Check if email already has a pending/approved request
      const { data: existing } = await supabase
        .from("partner_requests" as any)
        .select("id, status")
        .eq("contact_email", form.contactEmail.trim())
        .in("status", ["pending", "approved"]);

      if (existing && (existing as any[]).length > 0) {
        setEmailError("Já existe uma solicitação com este e-mail. Se já foi aprovada, acesse pelo login.");
        setIsLoading(false);
        return;
      }

      // Check if email is already registered as a partner org
      const { data: existingOrg } = await supabase
        .from("partner_orgs" as any)
        .select("id")
        .eq("contact_email", form.contactEmail.trim())
        .limit(1);

      if (existingOrg && (existingOrg as any[]).length > 0) {
        setEmailError("Este e-mail já está vinculado a uma organização parceira.");
        setIsLoading(false);
        return;
      }

      const { error } = await supabase.from("partner_requests" as any).insert({
        company_name: form.companyName.trim(),
        contact_name: form.contactName.trim(),
        contact_email: form.contactEmail.trim(),
        contact_phone: form.contactPhone.trim() || null,
        website: form.website.trim() || null,
        city: form.city.trim() || null,
        state: form.state.trim() || null,
        industry: form.industry.trim() || null,
        partner_types: selectedTypes,
        message: form.message.trim() || null,
      });
      if (error) throw error;
      setSubmitted(true);
    } catch (err: any) {
      toast({
        title: "Erro ao enviar",
        description: err.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="space-y-4"
        >
          <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto" />
          <h2 className="font-heading text-2xl font-bold text-foreground">
            Solicitação Enviada!
          </h2>
          <p className="text-muted-foreground max-w-md">
            Recebemos sua solicitação de parceria! Estamos analisando e
            entraremos em contato em até <strong>3 dias úteis</strong>.
          </p>
          <p className="text-sm text-muted-foreground">
            Você receberá uma resposta no e-mail informado.
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="py-2">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-1 mb-6"
      >
        <h2 className="font-heading text-xl font-bold text-foreground">
          Solicitar Parceria
        </h2>
        <p className="text-sm text-muted-foreground">
          Preencha os dados abaixo para se tornar parceiro do Corre Digital.
        </p>
      </motion.div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Company Info */}
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="companyName">Nome da organização *</Label>
            <Input
              id="companyName"
              placeholder="Empresa ABC"
              value={form.companyName}
              onChange={(e) => handleChange("companyName", e.target.value)}
              maxLength={100}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="industry">Segmento / Indústria</Label>
            <Input
              id="industry"
              placeholder="Ex: Tecnologia, Educação, Saúde..."
              value={form.industry}
              onChange={(e) => handleChange("industry", e.target.value)}
              maxLength={100}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="contactName">Nome do responsável *</Label>
              <Input
                id="contactName"
                placeholder="João Silva"
                value={form.contactName}
                onChange={(e) => handleChange("contactName", e.target.value)}
                maxLength={100}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="contactEmail">E-mail corporativo *</Label>
              <Input
                id="contactEmail"
                type="email"
                placeholder="joao@empresa.com"
                value={form.contactEmail}
                onChange={(e) => {
                  handleChange("contactEmail", e.target.value);
                  if (emailError) setEmailError("");
                }}
                maxLength={255}
                className={emailError ? "border-destructive" : ""}
              />
              {emailError && <p className="text-xs text-destructive">{emailError}</p>}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="contactPhone">Telefone / WhatsApp</Label>
              <Input
                id="contactPhone"
                placeholder="(11) 99999-9999"
                value={form.contactPhone}
                onChange={(e) => handleChange("contactPhone", e.target.value)}
                maxLength={20}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="website">Site ou LinkedIn</Label>
              <Input
                id="website"
                placeholder="https://..."
                value={form.website}
                onChange={(e) => handleChange("website", e.target.value)}
                maxLength={200}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="city">Cidade</Label>
              <Input
                id="city"
                placeholder="São Paulo"
                value={form.city}
                onChange={(e) => handleChange("city", e.target.value)}
                maxLength={50}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="state">UF</Label>
              <Input
                id="state"
                placeholder="SP"
                value={form.state}
                onChange={(e) => handleChange("state", e.target.value)}
                maxLength={2}
              />
            </div>
          </div>
        </div>

        {/* Partner Types */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">
            Tipo de parceria desejada *
          </Label>
          <p className="text-xs text-muted-foreground">
            Selecione uma ou mais opções
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
            {PARTNER_TYPES.map((type) => (
              <label
                key={type.id}
                className="flex items-center gap-2.5 p-2.5 rounded-xl border border-border hover:bg-muted/50 cursor-pointer transition-colors"
              >
                <Checkbox
                  checked={selectedTypes.includes(type.id)}
                  onCheckedChange={() => toggleType(type.id)}
                />
                <span className="text-sm text-foreground">{type.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Message */}
        <div className="space-y-1.5">
          <Label htmlFor="message">
            Como quer apoiar o Corre Digital?
          </Label>
          <Textarea
            id="message"
            placeholder="Conte um pouco sobre sua motivação e como gostaria de contribuir..."
            value={form.message}
            onChange={(e) => handleChange("message", e.target.value)}
            rows={3}
            maxLength={1000}
          />
        </div>

        <Button
          type="submit"
          variant="hero"
          size="xl"
          className="w-full"
          disabled={!canSubmit || isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Enviando...
            </>
          ) : (
            <>
              <Building2 className="w-5 h-5" />
              Enviar Solicitação
            </>
          )}
        </Button>

        <p className="text-xs text-muted-foreground text-center">
          Sua solicitação será analisada pela equipe do Corre Digital. Não
          criamos contas automaticamente — o acesso é liberado após aprovação.
        </p>
      </form>
    </div>
  );
}
